/**
 * AGI-428: the per-workspace notice about Meta's WhatsApp service-message
 * pricing change (1-oct-2026), as it arrives from
 * `GET {V2}workspace/{wid}/notices` (bff-web proxy over
 * workspace-service's `workspace_notices` table).
 *
 * 🔴 THE FRONT ONLY PAINTS. Every figure in the payload is already converted
 * and rounded by the seeding command (`workspace:seed-notice`): MXN is an
 * integer with IVA included, USD carries two decimals, and both are MONTHLY
 * (the 28-day window is multiplied by 30.4375/28 upstream). Recomputing any
 * of it here would create a second definition of the customer's estimate, and
 * the two would disagree the day the rate or the IVA changes, which is
 * exactly why they are command parameters and not code.
 *
 * The wire is snake_case (it is a jsonb column seeded from a CSV); the
 * domain shape below is camelCase like every other type in the repo. The
 * mapping happens once, inside `parseMetaPricingNotice`.
 */

/** The notice key the seeding command writes. Rows with any other key are not ours. */
export const META_PRICING_NOTICE_KEY = 'meta-pricing-2026-10';

/** The alert family id in `ALERT_REGISTRY`. Same string on purpose: one notice, one family. */
export const META_PRICING_ALERT_ID = META_PRICING_NOTICE_KEY;

/**
 * AGI-428, message 2 (25-sep): the follow-up with the best-practices video.
 * Same audience, same payload shape and the same copy variant each workspace
 * got in message 1, seeded under its own key so its "no volver a mostrar",
 * session mark and bell acknowledgment start clean: what someone did with
 * message 1 must not silence a message they have not seen.
 */
export const META_PRICING_TIPS_NOTICE_KEY = 'meta-pricing-2026-10-tips';
export const META_PRICING_TIPS_ALERT_ID = META_PRICING_TIPS_NOTICE_KEY;

/**
 * AGI-428, message 3 (6-oct): the new balance. Credits now show as money that
 * does not expire, the free window for Click to WhatsApp ads went from 72
 * hours to 7 days, and Dany's video walks through both. Same 779 workspaces
 * and the same copy variant as messages 1 and 2, under its own key for the
 * same reason message 2 has one: its marks have to start clean.
 *
 * It only reaches a workspace that actually SEES the balance
 * (`gateMetaPricingNoticeOnWallet`): the copy says "desde hoy tus creditos se
 * muestran como saldo", which is false for one that does not.
 */
export const META_PRICING_BALANCE_NOTICE_KEY = 'meta-pricing-2026-10-balance';
export const META_PRICING_BALANCE_ALERT_ID = META_PRICING_BALANCE_NOTICE_KEY;

/** The three families, for the consumers that treat them alike (bell, auto-open). */
export const META_PRICING_ALERT_IDS: readonly string[] = [
  META_PRICING_ALERT_ID,
  META_PRICING_TIPS_ALERT_ID,
  META_PRICING_BALANCE_ALERT_ID,
];

/**
 * `impact` is message 1 (the estimate), `tips` is message 2 (the
 * best-practices video), `balance` is message 3 (the new balance video).
 */
export const META_PRICING_MESSAGES = ['impact', 'tips', 'balance'] as const;
export type tMetaPricingMessage = (typeof META_PRICING_MESSAGES)[number];

/** The message a row carries is a fact about its KEY, never about its payload. */
export function getMetaPricingMessage(key: string): tMetaPricingMessage {
  if (key === META_PRICING_BALANCE_NOTICE_KEY) return 'balance';
  return key === META_PRICING_TIPS_NOTICE_KEY ? 'tips' : 'impact';
}

/** Where the workspace's spend lands after 1-oct, as the analysis classified it. */
export const META_PRICING_SEGMENTS = [
  'sube_extremo',
  'sube_moderado',
  'cobro_nuevo',
  'le_baja',
  'sigue_sin_pagar',
  'igual',
] as const;
export type tMetaPricingSegment = (typeof META_PRICING_SEGMENTS)[number];

/**
 * Which of the five approved copies this workspace receives. NOT derivable
 * from `segment`: "cuenta clave" is a commercial decision (top 10% by plan
 * value, Ariel's list) that cuts across segments, so the seeding command
 * decides and the payload carries the answer.
 */
export const META_PRICING_COPY_VARIANTS = [
  'sube_extremo',
  'cuenta_clave',
  'cobro_nuevo',
  'sube_moderado',
  'baja_urgencia',
] as const;
export type tMetaPricingCopyVariant =
  (typeof META_PRICING_COPY_VARIANTS)[number];

/**
 * Drives the CTA (1:1 session / webinar / help article) and whether the tools
 * block renders. Carried rather than derived from `copyVariant` for the same
 * reason: Growth can move a copy between urgencies without a deploy.
 */
export const META_PRICING_URGENCIES = ['alta', 'media', 'baja'] as const;
export type tMetaPricingUrgency = (typeof META_PRICING_URGENCIES)[number];

/** The workspace's BILLING currency, not a display preference. */
export const META_PRICING_CURRENCIES = ['USD', 'MXN'] as const;
export type tMetaPricingCurrency = (typeof META_PRICING_CURRENCIES)[number];

export type tMetaPricingPayload = {
  version: number;
  segment: tMetaPricingSegment;
  copyVariant: tMetaPricingCopyVariant;
  urgency: tMetaPricingUrgency;
  keyAccount: boolean;
  currency: tMetaPricingCurrency;
  /** Monthly, already in `currency`. `0` is a real value (`cobro_nuevo`). */
  amountToday: number;
  amountOctober: number;
  /** The raw 28-day USD readings the estimate came from, for support. */
  usdToday28d: number;
  usdOctober28d: number;
  businessName: string;
  /**
   * Empty while a link is still pending (the webinar, urgency media). An
   * empty string is NOT a defect: the row ships, the button does not render,
   * and the link arrives by a data update with no deploy.
   */
  ctaUrl: string;
  /**
   * Message 1: urgency media only, null everywhere else. Messages 2 and 3:
   * every row (the video IS the message), set by a data update after the
   * seed, so a missing one hides the player instead of breaking the notice.
   */
  videoUrl: string | null;
  batch: number;
  source: string;
};

export type tMetaPricingNotice = {
  key: string;
  payload: tMetaPricingPayload;
  /** ISO 8601 UTC. */
  visibleFrom: string;
  expiresAt: string;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asMember<T extends string>(
  value: unknown,
  members: readonly T[],
): T | null {
  return typeof value === 'string' && (members as readonly string[]).includes(value)
    ? (value as T)
    : null;
}

/**
 * The runtime guard the repo's enum rule demands (CLAUDE.md, "Runtime Enum
 * Validation for External Input"): this payload is seeded from a CSV by a
 * one-shot command, so a typo in a column reaches the browser as a plain
 * string that `as tMetaPricingSegment` would wave through and the renderer
 * would then index copy tables with.
 *
 * Returns `null` for anything it cannot vouch for, and the caller treats that
 * EXACTLY like "this workspace has no notice": never a throw, never a
 * partially-filled card. A half-rendered price estimate is worse than none.
 */
export function parseMetaPricingPayload(
  raw: unknown,
): tMetaPricingPayload | null {
  const source = asRecord(raw);
  if (!source) return null;

  const segment = asMember(source.segment, META_PRICING_SEGMENTS);
  const copyVariant = asMember(source.copy_variant, META_PRICING_COPY_VARIANTS);
  const urgency = asMember(source.urgency, META_PRICING_URGENCIES);
  const currency = asMember(source.currency, META_PRICING_CURRENCIES);
  if (!segment || !copyVariant || !urgency || !currency) return null;

  const version = asFiniteNumber(source.version);
  const amountToday = asFiniteNumber(source.amount_today);
  const amountOctober = asFiniteNumber(source.amount_october);
  const usdToday28d = asFiniteNumber(source.usd_today_28d);
  const usdOctober28d = asFiniteNumber(source.usd_october_28d);
  const batch = asFiniteNumber(source.batch);
  if (
    version === null ||
    amountToday === null ||
    amountOctober === null ||
    usdToday28d === null ||
    usdOctober28d === null ||
    batch === null
  ) {
    return null;
  }

  if (typeof source.key_account !== 'boolean') return null;
  if (typeof source.business_name !== 'string') return null;
  // Empty is legitimate (a pending link); a non-string is not.
  if (typeof source.cta_url !== 'string') return null;
  if (typeof source.source !== 'string') return null;
  const videoUrl = source.video_url;
  if (videoUrl !== null && typeof videoUrl !== 'string') return null;

  return {
    version,
    segment,
    copyVariant,
    urgency,
    keyAccount: source.key_account,
    currency,
    amountToday,
    amountOctober,
    usdToday28d,
    usdOctober28d,
    businessName: source.business_name,
    ctaUrl: source.cta_url,
    videoUrl: videoUrl ?? null,
    batch,
    source: source.source,
  };
}

/** One row of the endpoint's `data` array, validated end to end. */
export function parseMetaPricingNotice(raw: unknown): tMetaPricingNotice | null {
  const row = asRecord(raw);
  if (!row) return null;
  if (typeof row.key !== 'string' || row.key.trim().length === 0) return null;
  if (typeof row.visibleFrom !== 'string') return null;
  if (typeof row.expiresAt !== 'string') return null;

  const payload = parseMetaPricingPayload(row.payload);
  if (!payload) return null;

  return {
    key: row.key,
    payload,
    visibleFrom: row.visibleFrom,
    expiresAt: row.expiresAt,
  };
}

/**
 * The client-side expiry net. The backend already filters on `expires_at`, so
 * this only catches the case where the two clocks disagree: a tab left open
 * across the 5-oct cut-off, or a browser whose clock runs ahead. An
 * unparseable date is NOT treated as expired: the fail-safe direction is to
 * keep showing a notice the backend chose to serve.
 */
export function isMetaPricingNoticeExpired(
  notice: tMetaPricingNotice,
  now: number,
): boolean {
  const expiresAt = Date.parse(notice.expiresAt);
  if (Number.isNaN(expiresAt)) return false;
  return expiresAt <= now;
}

/** The first row with `key`, parsed and unexpired, or `null`. */
function selectLiveRow(
  data: unknown[],
  key: string,
  now: number,
): tMetaPricingNotice | null {
  for (const raw of data) {
    const row = asRecord(raw);
    if (!row || row.key !== key) continue;
    const notice = parseMetaPricingNotice(row);
    if (!notice) return null;
    if (isMetaPricingNoticeExpired(notice, now)) return null;
    return notice;
  }
  return null;
}

/**
 * The keys this source reads, in the order they win. The newest message
 * first: each one follows up on the one before, so while it is live showing
 * both would be two bell rows and two automatic modals about one change.
 */
export const META_PRICING_NOTICE_KEYS_BY_PRIORITY: readonly string[] = [
  META_PRICING_BALANCE_NOTICE_KEY,
  META_PRICING_TIPS_NOTICE_KEY,
  META_PRICING_NOTICE_KEY,
];

/**
 * The endpoint's whole `data` array in, this workspace's live notice out.
 *
 * The newest live message wins (3, then 2, then 1). A row the guard refuses
 * FALLS BACK to the previous message rather than to nothing: a bad re-seed
 * then degrades to a notice everyone already saw (the source still warns),
 * and switching a message off (`expires_at = now()`) brings the previous one
 * back with no deploy, while it is still live.
 *
 * Everything else resolves to `null`: an empty list, a row for another notice
 * key, a message 1 payload the guard refused. The source treats all of them
 * as "no notice", which is why a bad seed can never break the app.
 */
export function selectMetaPricingNotice(
  data: unknown,
  now: number,
): tMetaPricingNotice | null {
  if (!Array.isArray(data)) return null;
  for (const key of META_PRICING_NOTICE_KEYS_BY_PRIORITY) {
    const notice = selectLiveRow(data, key, now);
    if (notice) return notice;
  }
  return null;
}

/**
 * Whether two resolved notices say the same thing. The source publishes a new
 * reference only when this is false, so a focus refetch that returns the same
 * row does not re-render every consumer memoized on the snapshot.
 */
export function isSameMetaPricingNotice(
  a: tMetaPricingNotice | null,
  b: tMetaPricingNotice | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (
    a.key !== b.key ||
    a.visibleFrom !== b.visibleFrom ||
    a.expiresAt !== b.expiresAt
  ) {
    return false;
  }
  const left = a.payload;
  const right = b.payload;
  return (
    left.version === right.version &&
    left.segment === right.segment &&
    left.copyVariant === right.copyVariant &&
    left.urgency === right.urgency &&
    left.keyAccount === right.keyAccount &&
    left.currency === right.currency &&
    left.amountToday === right.amountToday &&
    left.amountOctober === right.amountOctober &&
    left.usdToday28d === right.usdToday28d &&
    left.usdOctober28d === right.usdOctober28d &&
    left.businessName === right.businessName &&
    left.ctaUrl === right.ctaUrl &&
    left.videoUrl === right.videoUrl &&
    left.batch === right.batch &&
    left.source === right.source
  );
}
